'use client';

import { useEffect, useState, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { livePriceService, type PriceUpdate } from '@/lib/live-price-service';

interface LivePriceIndicatorProps {
  itemId: string;
  basePrice: number;
  volatility?: 'low' | 'medium' | 'high';
  showTrend?: boolean;
  updateInterval?: number;
}

const LivePriceIndicator = ({
  itemId,
  basePrice,
  volatility = 'medium',
  showTrend = true,
  updateInterval = 15000,
}: LivePriceIndicatorProps) => {
  const [currentPrice, setCurrentPrice] = useState(basePrice);
  const [priceChange, setPriceChange] = useState(0);
  const [trend, setTrend] = useState<'up' | 'down' | 'stable'>('stable');
  const [isUpdating, setIsUpdating] = useState(false);

  const handlePriceUpdate = useCallback((update: PriceUpdate) => {
    setIsUpdating(true);
    setCurrentPrice(update.currentPrice);
    setPriceChange(update.percentageChange);
    setTrend(update.trend);

    // Reset updating indicator after animation
    setTimeout(() => setIsUpdating(false), 500);
  }, []);

  useEffect(() => {
    // Initialize price tracking
    livePriceService.initializePriceTracking(itemId, basePrice, volatility);

    // Start live updates
    livePriceService.startLivePriceUpdates(
      itemId,
      handlePriceUpdate,
      updateInterval
    );

    return () => {
      livePriceService.stopLivePriceUpdates(itemId);
    };
  }, [itemId, basePrice, volatility, updateInterval, handlePriceUpdate]);

  const getTrendColor = () => {
    if (trend === 'down') return 'text-success';
    if (trend === 'up') return 'text-error';
    return 'text-muted-foreground';
  };

  const getTrendIcon = () => {
    if (trend === 'down') return 'ArrowTrendingDownIcon';
    if (trend === 'up') return 'ArrowTrendingUpIcon';
    return 'MinusIcon';
  };

  return (
    <div className="flex items-center space-x-2">
      <div className="flex items-center space-x-1">
        <span
          className={`text-2xl font-heading font-bold text-primary transition-all duration-300 ${
            isUpdating ? 'scale-110' : 'scale-100'
          }`}
        >
          £{currentPrice.toLocaleString()}
        </span>
        {isUpdating && (
          <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
        )}
      </div>

      {showTrend && Math.abs(priceChange) > 0.5 && (
        <div className={`flex items-center space-x-1 ${getTrendColor()}`}>
          <Icon name={getTrendIcon() as any} size={16} />
          <span className="text-sm font-bold">
            {Math.abs(priceChange).toFixed(1)}%
          </span>
        </div>
      )}

      <div className="flex items-center space-x-1 text-xs text-muted-foreground">
        <Icon name="ClockIcon" size={12} />
        <span>Live</span>
      </div>
    </div>
  );
};

export default LivePriceIndicator;